import React, { useEffect, useState, useMemo } from "react";
import config from "../../config";
import GuestHistoryBadge from "./GuestHistoryBadge";
import {
  Paper,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Grid,
  Button,
  Avatar,
  Chip,
  InputAdornment,
  Tooltip,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import { motion } from "framer-motion";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import PublicIcon from "@mui/icons-material/Public";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const company = JSON.parse(sessionStorage.getItem("companyDetails"));

// Deterministic, pleasant avatar gradient per guest (stable across renders).
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#667eea,#764ba2)",
  "linear-gradient(135deg,#f093fb,#f5576c)",
  "linear-gradient(135deg,#4facfe,#00f2fe)",
  "linear-gradient(135deg,#43e97b,#38f9d7)",
  "linear-gradient(135deg,#fa709a,#fee140)",
  "linear-gradient(135deg,#30cfd0,#330867)",
  "linear-gradient(135deg,#a8edea,#fed6e3)",
  "linear-gradient(135deg,#ff9a9e,#fad0c4)",
  "linear-gradient(135deg,#5ee7df,#b490ca)",
  "linear-gradient(135deg,#f7971e,#ffd200)",
];
const gradientFor = (str) => {
  const s = (str || "G").toUpperCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i)) % AVATAR_GRADIENTS.length;
  return AVATAR_GRADIENTS[h];
};
const initials = (f, l) =>
  `${(f || "").trim()[0] || ""}${(l || "").trim()[0] || ""}`.toUpperCase() || "G";

// Compact stat card for the summary row.
function StatCard({ icon, label, value, gradient }) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 300 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          p: 2.5,
          color: "#fff",
          background: gradient,
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
        }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.22)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 13, opacity: 0.9 }}>{label}</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 26, lineHeight: 1.1 }}>
            {value}
          </Typography>
        </Box>
      </Paper>
    </motion.div>
  );
}

export default function GuestReport() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchName, setSearchName] = useState("");
  const navigate = useNavigate();

  const token = sessionStorage.getItem("token");

  const fetchGuests = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/GetAllguestReports/${encodeURIComponent(
          company?.id
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setGuests(data || []);
    } catch (err) {
      console.error("Error fetching guest report", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔍 Filter by name / mobile / city.
  const filteredGuests = useMemo(() => {
    const q = searchName.toLowerCase().trim();
    if (!q) return guests;
    return guests.filter((g) =>
      `${g.firstName || ""} ${g.lastName || ""} ${g.mobileNumber || ""} ${
        g.city || ""
      }`
        .toLowerCase()
        .includes(q)
    );
  }, [guests, searchName]);

  const stats = useMemo(() => {
    const cities = new Set(
      guests.map((g) => (g.city || "").trim().toLowerCase()).filter(Boolean)
    );
    const withEmail = guests.filter((g) => (g.email || "").trim()).length;
    return { total: guests.length, cities: cities.size, withEmail };
  }, [guests]);

  // 📤 Export to Excel
  const exportToExcel = () => {
    if (filteredGuests.length === 0) {
      toast.info("No data to export");
      return;
    }
    const formatted = filteredGuests.map((g, i) => ({
      "#": i + 1,
      "First Name": g.firstName,
      "Last Name": g.lastName,
      Email: g.email,
      Mobile: g.mobileNumber,
      City: g.city,
      Address: g.address,
    }));
    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "GuestReport");
    XLSX.writeFile(wb, "GuestReport.xlsx");
  };

  if (loading)
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="70vh"
        gap={2}>
        <CircularProgress size={56} thickness={4} />
        <Typography color="text.secondary">Loading guests…</Typography>
      </Box>
    );

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        minHeight: "100vh",
        background:
          "linear-gradient(180deg,#eef2ff 0%,#f8fafc 240px,#f8fafc 100%)",
      }}>
      {/* ── Hero header ─────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 5,
            p: { xs: 3, md: 4 },
            mb: 3,
            position: "relative",
            overflow: "hidden",
            color: "#fff",
            background:
              "linear-gradient(120deg,#0a2540 0%,#16335c 55%,#1d4ed8 100%)",
            boxShadow: "0 18px 50px rgba(10,37,64,0.35)",
          }}>
          {/* decorative blobs */}
          <Box
            sx={{
              position: "absolute",
              top: -60,
              right: -40,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -70,
              right: 120,
              width: 160,
              height: 160,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.06)",
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
            }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <GroupsIcon sx={{ fontSize: 34 }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
                Guest Report
              </Typography>
              <Typography sx={{ opacity: 0.85, mt: 0.5 }}>
                {company?.name ? `${company.name} · ` : ""}Every guest, their
                visit history & one-tap rebooking
              </Typography>
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {/* ── Stats ───────────────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<GroupsIcon />}
            label="Total Guests"
            value={stats.total}
            gradient="linear-gradient(135deg,#1d4ed8,#3b82f6)"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<PublicIcon />}
            label="Cities"
            value={stats.cities}
            gradient="linear-gradient(135deg,#7c3aed,#a855f7)"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard
            icon={<MarkEmailReadOutlinedIcon />}
            label="With Email"
            value={stats.withEmail}
            gradient="linear-gradient(135deg,#059669,#10b981)"
          />
        </Grid>
      </Grid>

      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          p: 1.5,
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          border: "1px solid #e8eef7",
          boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
        }}>
        <TextField
          placeholder="Search by name, mobile or city…"
          size="small"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94a3b8" }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 3, bgcolor: "#f8fafc" },
          }}
        />
        <Chip
          label={`${filteredGuests.length} shown`}
          sx={{ fontWeight: 700, bgcolor: "#eef2ff", color: "#1d4ed8" }}
        />
        <Tooltip title="Refresh">
          <IconButton onClick={fetchGuests} sx={{ bgcolor: "#f1f5f9" }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          startIcon={<FileDownloadIcon />}
          onClick={exportToExcel}
          sx={{
            borderRadius: 3,
            fontWeight: 700,
            textTransform: "none",
            background: "linear-gradient(135deg,#0a2540,#1d4ed8)",
            px: 2.5,
          }}>
          Export
        </Button>
      </Paper>

      {/* ── Guest cards ─────────────────────────────────────────── */}
      {filteredGuests.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            p: 8,
            textAlign: "center",
            border: "1px dashed #cbd5e1",
            color: "#94a3b8",
          }}>
          <GroupsIcon sx={{ fontSize: 60, mb: 1, opacity: 0.5 }} />
          <Typography fontWeight={600}>No guests found</Typography>
          <Typography variant="body2">
            Try a different search, or refresh.
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #e8eef7",
            boxShadow: "0 8px 30px rgba(15,23,42,0.06)",
            maxHeight: 640,
          }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {["#", "Guest", "Mobile", "Email", "Address", "History & Rebook"].map(
                  (h, idx) => (
                    <TableCell
                      key={h}
                      align={idx === 0 ? "center" : "left"}
                      sx={{
                        bgcolor: "#0a2540",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 13,
                        letterSpacing: 0.3,
                        whiteSpace: "nowrap",
                        borderBottom: "none",
                      }}>
                      {h}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredGuests.map((g, i) => {
                const name =
                  `${g.firstName || ""} ${g.lastName || ""}`.trim() || "Guest";
                return (
                  <TableRow
                    key={i}
                    hover
                    sx={{
                      "&:nth-of-type(even)": { bgcolor: "#f8fafc" },
                      "&:hover": { bgcolor: "#eef4ff !important" },
                      transition: "background .15s",
                    }}>
                    <TableCell
                      align="center"
                      sx={{ color: "#94a3b8", fontWeight: 700 }}>
                      {i + 1}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            fontSize: 15,
                            fontWeight: 800,
                            background: gradientFor(name),
                          }}>
                          {initials(g.firstName, g.lastName)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            fontWeight={700}
                            sx={{ color: "#0f172a" }}
                            noWrap>
                            {name}
                          </Typography>
                          {g.city && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.4,
                                color: "#64748b",
                              }}>
                              <LocationOnOutlinedIcon sx={{ fontSize: 13 }} />
                              <Typography variant="caption">{g.city}</Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <PhoneIphoneIcon sx={{ fontSize: 17, color: "#1d4ed8" }} />
                        <Typography variant="body2" sx={{ color: "#334155" }}>
                          {g.mobileNumber || "—"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <EmailOutlinedIcon sx={{ fontSize: 17, color: "#7c3aed" }} />
                        <Typography variant="body2" sx={{ color: "#334155" }}>
                          {g.email || "—"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#64748b",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}>
                        {g.address || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <GuestHistoryBadge
                        mobile={g.mobileNumber}
                        onBookAgain={(b) =>
                          navigate("/admin/manageinventory", {
                            state: { prefill: { guest: b } },
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
